import {zodResolver} from '@hookform/resolvers/zod';
import {Button, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React, {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {UserValidationStatusEnum} from 'src/api/ProactApiClient';
import {useAppDispatch} from 'src/redux/hooks';
import {useLazyCheckUserStatusQuery} from 'src/redux/queries/user';
import {setUserAccountStatus, setUsername} from 'src/redux/slices/authSlice';
import {z} from 'zod';

const schema = z.object({
  email: z.string().email('Please enter a valid email address.'),
});

type FormValues = z.infer<typeof schema>;

export interface VendorEmailCheckProps {
  onEmailValidated: (email: string, userExists: boolean) => void;
}

export const VendorEmailCheck: React.FC<VendorEmailCheckProps> = ({
  onEmailValidated,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const [checkUserStatus] = useLazyCheckUserStatusQuery();

  const {control, handleSubmit: handleFormSubmit} = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(schema),
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await checkUserStatus({
        email: values.email,
      }).unwrap();

      dispatch(setUserAccountStatus(response.status));
      dispatch(setUsername(values.email));

      // Check if user already exists
      const userExists =
        response.status !== UserValidationStatusEnum.DoesNotExist;

      onEmailValidated(values.email, userExists);
    } catch (error) {
      console.error('Email check error:', error);
      setError(
        'An error occurred while verifying your email address. Please check your connection and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <Stack>
          <Text size="sm" c="dimmed">
            Welcome! Your signup link allows you to sign up for and log into
            Proact EHS. Please enter your email address to continue with the
            signup process.
          </Text>

          <Controller
            name="email"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Email"
                placeholder="your.email@example.com"
                {...field}
                error={fieldState.error?.message}
                autoFocus
              />
            )}
          />

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

          <Button type="submit" loading={isLoading} fullWidth>
            Continue
          </Button>
        </Stack>
      </form>
    </>
  );
};
